import type {
  WorkspaceNavigationOutcome,
  WorkspaceNavigationRequest,
} from '@streamlain/copilot-realtime-contract';

import {
  isSemanticDomElementRendered,
  isSemanticDomElementVisible,
} from './semantic-dom-context';
import { withSurfaceInteractionOrigin } from './surface-interactions';
import { mapWorkspaceContextIssueToMessageKey } from './workspace-context-messages';
import {
  NavigationActivationError,
  type UiNavigationBinding,
  type UiNavigationDefinition,
} from './workspace-navigation-catalog';
import type { WorkspaceViewContextService } from './workspace-view-context';

export interface WorkspaceNavigationPort {
  readonly framework: WorkspaceViewContextService['framework'];
  invoke(
    request: WorkspaceNavigationRequest,
    signal?: AbortSignal
  ): Promise<WorkspaceNavigationOutcome>;
}
type Failure = Exclude<WorkspaceNavigationOutcome['status'], 'reached'>;
const failure = (status: Failure): WorkspaceNavigationOutcome => ({
  status,
  messageKey: mapWorkspaceContextIssueToMessageKey(status),
});

const MAX_RETAINED_REQUESTS = 128;

/** Registered parents and terminal targets use only their normal native bindings. */
export class WorkspaceNavigationExecutor implements WorkspaceNavigationPort {
  private disposed = false;
  private operation: { controller: AbortController; clicked: boolean } | null =
    null;
  private readonly requests = new Map<
    string,
    {
      intent: WorkspaceNavigationRequest;
      result: Promise<WorkspaceNavigationOutcome>;
    }
  >();
  constructor(private readonly context: WorkspaceViewContextService) {}

  get framework() {
    return this.context.framework;
  }

  invoke(
    request: WorkspaceNavigationRequest,
    signal?: AbortSignal
  ): Promise<WorkspaceNavigationOutcome> {
    const retained = this.requests.get(request.requestId);
    if (retained) {
      const previous = retained.intent;
      const argsKey = (args: WorkspaceNavigationRequest['args']) =>
        JSON.stringify(
          Object.entries(args).sort(([a], [b]) => a.localeCompare(b))
        );
      return previous.workspaceId === request.workspaceId &&
        previous.viewId === request.viewId &&
        previous.actionId === request.actionId &&
        previous.revision.epoch === request.revision.epoch &&
        previous.revision.sequence === request.revision.sequence &&
        argsKey(previous.args) === argsKey(request.args)
        ? retained.result
        : Promise.resolve(failure('blocked'));
    }
    if (this.disposed) return Promise.resolve(failure('stale'));
    if (this.operation || !request.requestId.trim())
      return Promise.resolve(failure('blocked'));
    // Replays of recent requests stay idempotent; only one operation runs at a
    // time, so the oldest retained request is always settled and can go.
    if (this.requests.size >= MAX_RETAINED_REQUESTS)
      this.requests.delete(this.requests.keys().next().value!);
    // Own an immutable copy for the whole animation; callers cannot retarget it.
    const intent: WorkspaceNavigationRequest = {
      ...request,
      revision: { ...request.revision },
      args: Object.freeze(
        Object.fromEntries(
          Object.entries(request.args).map(([key, value]) => [
            key,
            Array.isArray(value) ? Object.freeze([...value]) : value,
          ])
        )
      ),
    };
    const operation = { controller: new AbortController(), clicked: false };
    this.operation = operation;
    const result = this.execute(intent, signal, operation)
      .catch(() => failure('blocked'))
      .finally(() => {
        if (this.operation === operation) this.operation = null;
      });
    this.requests.set(request.requestId, { intent, result });
    return result;
  }

  private async execute(
    intent: WorkspaceNavigationRequest,
    signal: AbortSignal | undefined,
    operation: { controller: AbortController; clicked: boolean }
  ): Promise<WorkspaceNavigationOutcome> {
    const request = this.context.navigation.rebase(intent);
    const resolved = this.context.navigation.resolve(request);
    if (!resolved.ok) return failure(resolved.reason);
    const store = this.context.getNavigationPresentation(request);
    if (!store) return failure('blocked');
    const sources = new Map<
      string,
      {
        definition: UiNavigationDefinition;
        parents: readonly string[];
        activate: UiNavigationDefinition['activate'];
        readTarget: UiNavigationDefinition['readTarget'];
      }
    >();
    const parents: string[] = [],
      visiting = new Set<string>();
    const capture = (id: string) => {
      if (visiting.has(id) || sources.size >= 128)
        throw new NavigationActivationError('blocked');
      if (sources.has(id)) return;
      const source = this.context.navigation.resolveParent(request, id);
      if (!source.ok) throw new NavigationActivationError(source.reason);
      visiting.add(id);
      const { definition } = source;
      sources.set(id, {
        definition,
        parents: [...definition.parentIds],
        activate: definition.activate,
        readTarget: definition.readTarget,
      });
      for (const parent of definition.parentIds) capture(parent);
      visiting.delete(id);
      if (id !== request.actionId) parents.push(id);
    };
    try {
      capture(request.actionId);
    } catch (error) {
      return failure(
        error instanceof NavigationActivationError ? error.reason : 'blocked'
      );
    }
    const usable = (binding: UiNavigationBinding) =>
      binding.root.isConnected &&
      binding.element.isConnected &&
      (!binding.element.closest('[data-workspace-view-id]') ||
        binding.element
          .closest('[data-workspace-view-id]')
          ?.getAttribute('data-workspace-view-id') === request.viewId) &&
      isSemanticDomElementVisible(binding.element, request) &&
      isSemanticDomElementVisible(binding.geometryElement, request) &&
      !binding.element.closest(':disabled,[aria-disabled="true"],[inert]');
    if (
      !parents.length &&
      (!resolved.binding ||
        !isSemanticDomElementRendered(resolved.binding.element, request))
    )
      return failure('blocked');
    const initial = this.context.snapshot(request, request.revision);
    if (!initial.ok)
      return failure(initial.reason === 'stale' ? 'stale' : 'blocked');
    const route = initial.snapshot.route,
      refs = JSON.stringify(initial.snapshot.refs);
    const initialRect =
      resolved.binding && usable(resolved.binding)
        ? resolved.binding.geometryElement.getBoundingClientRect()
        : null;
    let revision = request.revision,
      allowRevealRevision = false,
      validating = false;
    const source = (id: string) => {
      const current = this.context.navigation.resolveParent(request, id),
        captured = sources.get(id);
      if (
        !current.ok ||
        !captured ||
        current.definition !== captured.definition ||
        current.definition.activate !== captured.activate ||
        current.definition.readTarget !== captured.readTarget ||
        JSON.stringify(current.definition.parentIds) !==
          JSON.stringify(captured.parents)
      )
        throw new NavigationActivationError('stale');
      return current;
    };
    const verify = () => {
      if (validating) return;
      validating = true;
      try {
        for (const id of sources.keys()) source(id);
        const read = this.context.snapshot(
          request,
          allowRevealRevision ? undefined : revision
        );
        if (!read.ok)
          throw new NavigationActivationError(
            read.reason === 'stale' ? 'stale' : 'blocked'
          );
        // Disclosure may change DOM/regions, never the underlying route or domain selection.
        if (
          allowRevealRevision &&
          (read.snapshot.route !== route ||
            JSON.stringify(read.snapshot.refs) !== refs)
        )
          throw new NavigationActivationError('stale');
        revision = read.snapshot.revision;
        const fresh = this.context.navigation.resolve({ ...request, revision });
        if (!fresh.ok) throw new NavigationActivationError(fresh.reason);
      } finally {
        validating = false;
      }
    };
    let reason: Failure = 'stale',
      clicking = false,
      outcome: WorkspaceNavigationOutcome | null = null;
    const stop = (status: Failure) => {
      reason = status;
      operation.controller.abort();
    };
    const abort = () => stop('stale');
    if (signal?.aborted) return failure('stale');
    signal?.addEventListener('abort', abort, { once: true });
    const document = window.document;
    // Only the user's own devices take the hand back. What the app dispatches itself while the
    // target settles (a canvas clicks its root when it mounts) is no intervention.
    const userIntervention = (event: Event) => {
      if (event.isTrusted && !clicking) stop('stale');
    };
    document.addEventListener('click', userIntervention, true);
    document.addEventListener('pointerdown', userIntervention, true);
    document.addEventListener('keydown', userIntervention, true);
    const timeout = window.setTimeout(() => stop('timeout'), 15_000);
    let notify: (() => void) | undefined,
      frame: number | null = null;
    const readReached = () => {
      if (!operation.clicked || clicking || operation.controller.signal.aborted)
        return;
      if (this.context.workspaceId !== request.workspaceId) {
        stop('stale');
        return;
      }
      let target: ReturnType<typeof resolved.definition.readTarget>;
      try {
        target = resolved.definition.readTarget();
      } catch {
        stop('blocked');
        return;
      }
      if (!target) return;
      if (target.workspaceId !== request.workspaceId) {
        stop('stale');
        return;
      }
      const read = this.context.snapshot(target, target.revision);
      if (!read.ok) {
        if (read.reason === 'stale') stop('stale');
        return;
      }
      const state = read.snapshot;
      if (
        target.route !== state.route ||
        target.surfaceId !== state.surfaceId ||
        target.title !== state.title ||
        (target.focusRef &&
          !state.refs.some(
            ref =>
              ref.kind === target.focusRef!.kind &&
              ref.id === target.focusRef!.id &&
              ref.docId === target.focusRef!.docId
          ))
      )
        return;
      outcome = { status: 'reached', target, context: state };
      notify?.();
    };
    const subscription = this.context.changes$.subscribe(() => {
      if (clicking) return;
      if (!operation.clicked) {
        try {
          verify();
        } catch (error) {
          stop(
            error instanceof NavigationActivationError
              ? error.reason
              : 'blocked'
          );
        }
        notify?.();
      } else readReached();
    });
    const wake = () => notify?.();
    operation.controller.signal.addEventListener('abort', wake);
    const observer = new MutationObserver(() => {
      if (operation.clicked) readReached();
      else notify?.();
    });
    observer.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
    });
    const wait = (ready: () => boolean, framesOnly = false) =>
      new Promise<void>((resolve, reject) => {
        let finished = false;
        const check = () => {
          if (finished) return;
          try {
            if (operation.controller.signal.aborted)
              throw new NavigationActivationError('stale');
            if (!operation.clicked) verify();
            if (!ready()) return;
            finished = true;
            notify = undefined;
            if (frame !== null) window.cancelAnimationFrame(frame);
            frame = null;
            resolve();
          } catch (error) {
            finished = true;
            notify = undefined;
            if (frame !== null) window.cancelAnimationFrame(frame);
            frame = null;
            reject(error);
          }
        };
        const tick = () => {
          frame = null;
          check();
          if (!finished) frame = window.requestAnimationFrame(tick);
        };
        notify = () => {
          if (!framesOnly || operation.controller.signal.aborted) check();
        };
        tick();
      });
    const targetReached = (definition: UiNavigationDefinition) => {
      const target = definition.readTarget();
      if (!target) return false;
      if (
        target.workspaceId !== request.workspaceId ||
        target.viewId !== request.viewId
      )
        throw new NavigationActivationError('stale');
      const read = this.context.snapshot(target, target.revision);
      return (
        read.ok &&
        target.route === read.snapshot.route &&
        target.surfaceId === read.snapshot.surfaceId &&
        target.title === read.snapshot.title
      );
    };
    const readyBinding = async (id: string, revealed: boolean) => {
      let current = source(id);
      if (!current.binding && !revealed)
        throw new NavigationActivationError('blocked');
      if (!current.binding) await wait(() => !!source(id).binding);
      current = source(id);
      const binding = current.binding!;
      if (binding.element.closest(':disabled,[aria-disabled="true"],[inert]'))
        throw new NavigationActivationError('blocked');
      if (!isSemanticDomElementRendered(binding.element, request)) {
        if (!revealed) throw new NavigationActivationError('blocked');
        await wait(() =>
          isSemanticDomElementRendered(binding.element, request)
        );
      }
      if (!usable(binding)) {
        allowRevealRevision = true;
        revealed = true;
        binding.geometryElement.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'instant',
        });
        await wait(() => usable(binding));
        verify();
      }
      if (revealed) {
        allowRevealRevision = true;
        let previous: string | null = null;
        await wait(() => {
          if (source(id).binding !== binding)
            throw new NavigationActivationError('stale');
          if (!usable(binding)) {
            previous = null;
            return false;
          }
          const rect = binding.geometryElement.getBoundingClientRect();
          const geometry = JSON.stringify([
            rect.x,
            rect.y,
            rect.width,
            rect.height,
          ]);
          const settled = previous === geometry;
          previous = geometry;
          return settled;
        }, true);
      }
      allowRevealRevision = false;
      verify();
      return binding;
    };
    const click = async (
      id: string,
      binding: UiNavigationBinding,
      terminal: boolean
    ) => {
      const admitted = source(id);
      if (admitted.binding !== binding)
        throw new NavigationActivationError('stale');
      const definition = admitted.definition;
      const rect =
        terminal && initialRect
          ? initialRect
          : binding.geometryElement.getBoundingClientRect();
      const presentation = await store.presentNavigationClick(
        {
          controlId: id,
          kind: 'navigate',
          label: definition.label,
          rect: {
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height,
          },
        },
        () => {
          verify();
          const fresh = source(id);
          if (fresh.binding !== binding)
            throw new NavigationActivationError('stale');
          if (operation.controller.signal.aborted || !usable(binding))
            throw new NavigationActivationError('blocked');
          const geometry = binding.geometryElement.getBoundingClientRect();
          if (
            geometry.x !== rect.x ||
            geometry.y !== rect.y ||
            geometry.width !== rect.width ||
            geometry.height !== rect.height
          )
            throw new NavigationActivationError('stale');
          clicking = true;
          operation.clicked = terminal;
          allowRevealRevision = !terminal;
          try {
            binding.withClickArgs(terminal ? request.args : {}, () =>
              withSurfaceInteractionOrigin(binding.root, 'ai', () =>
                binding.element.click()
              )
            );
          } finally {
            clicking = false;
          }
        },
        operation.controller.signal
      );
      if (!presentation.clicked) throw new NavigationActivationError('stale');
    };
    try {
      let revealed = false;
      if (!resolved.binding || !usable(resolved.binding)) {
        for (const id of parents) {
          verify();
          const parent = source(id);
          if (targetReached(parent.definition)) {
            // Its owner is already open; only the render commit may still be pending.
            revealed = true;
            allowRevealRevision = true;
            continue;
          }
          const binding = await readyBinding(id, revealed);
          await click(id, binding, false);
          revealed = true;
          await wait(() => targetReached(source(id).definition));
          verify();
        }
      }
      const binding = await readyBinding(request.actionId, revealed);
      // A previously bound source cannot silently become another clickable object.
      if (
        resolved.binding &&
        (binding.element !== resolved.binding.element ||
          binding.root !== resolved.binding.root ||
          binding.geometryElement !== resolved.binding.geometryElement)
      )
        throw new NavigationActivationError('stale');
      await click(request.actionId, binding, true);
      readReached();
      if (!outcome && !operation.controller.signal.aborted) {
        await new Promise<void>(resolve => {
          notify = resolve;
          readReached();
          if (operation.controller.signal.aborted) resolve();
        });
      }
      return outcome ?? failure(reason);
    } catch (error) {
      return failure(
        operation.controller.signal.aborted
          ? reason
          : error instanceof NavigationActivationError
            ? error.reason
            : 'blocked'
      );
    } finally {
      window.clearTimeout(timeout);
      observer.disconnect();
      if (frame !== null) window.cancelAnimationFrame(frame);
      subscription.unsubscribe();
      signal?.removeEventListener('abort', abort);
      operation.controller.signal.removeEventListener('abort', wake);
      document.removeEventListener('click', userIntervention, true);
      document.removeEventListener('pointerdown', userIntervention, true);
      document.removeEventListener('keydown', userIntervention, true);
      store.rest();
    }
  }
  dispose(): void {
    this.disposed = true;
    // A completed terminal native click can still finish its existing owner readback.
    if (this.operation && !this.operation.clicked)
      this.operation.controller.abort();
  }
}
